export interface BodyTypeData {
  slug: string;
  name: string;
  pluralName: string;
  description: string;
  tagline: string;
  vehicleCount: number;
  priceRange: { min: number; max: number };
  popularBrands: string[];
  editorsPicks: { vehicle: string; price: number; reason: string }[];
  priceTiers: { label: string; count: number; searchParams: string }[];
  seo: { title: string; description: string };
  gradient: string;
}

export const BODY_TYPES: BodyTypeData[] = [
  {
    slug: 'sedans',
    name: 'Sedan',
    pluralName: 'Sedans',
    description: 'The sedan remains the gold standard of daily driving — blending comfort, efficiency, and style into a refined package. From spirited sport sedans to flagship luxury cruisers, the BESPOKIAN collection offers sedans for every discerning driver. Each arrives inspected, certified, and delivered to your door.',
    tagline: 'Refined comfort, effortless elegance',
    vehicleCount: 847,
    priceRange: { min: 1899000, max: 21500000 },
    popularBrands: ['bmw', 'mercedes-benz', 'lexus', 'toyota', 'honda'],
    editorsPicks: [
      { vehicle: '2025 BMW M340i xDrive', price: 4299000, reason: 'Best sport sedan value' },
      { vehicle: '2025 Mercedes-Benz S 580', price: 11890000, reason: 'Ultimate luxury flagship' },
      { vehicle: '2025 Lexus ES 350', price: 3249000, reason: 'Reliability meets refinement' },
      { vehicle: '2025 Genesis G70 3.3T', price: 3599000, reason: 'Luxury disruptor' },
    ],
    priceTiers: [
      { label: 'Under $25K', count: 189, searchParams: 'bodyType=sedan&priceMax=2500000' },
      { label: '$25K–$50K', count: 342, searchParams: 'bodyType=sedan&priceMin=2500000&priceMax=5000000' },
      { label: '$50K–$100K', count: 234, searchParams: 'bodyType=sedan&priceMin=5000000&priceMax=10000000' },
      { label: '$100K+', count: 82, searchParams: 'bodyType=sedan&priceMin=10000000' },
    ],
    seo: { title: 'Used Sedans for Sale | BESPOKIAN Auto', description: 'Browse 847 certified pre-owned sedans from BMW, Mercedes-Benz, Lexus, and more. Every sedan inspected, guaranteed, and delivered.' },
    gradient: 'from-[#1E2A3A] to-[#D4A853]/20',
  },
  {
    slug: 'suvs',
    name: 'SUV',
    pluralName: 'SUVs',
    description: 'From compact crossovers to full-size luxury SUVs, the sport utility vehicle dominates the American road for good reason. Versatility, capability, and commanding presence converge in our curated SUV collection. Whether you need three rows for the family or off-road prowess for the weekend, BESPOKIAN has your next SUV.',
    tagline: 'Command every road with confidence',
    vehicleCount: 1243,
    priceRange: { min: 2199000, max: 24900000 },
    popularBrands: ['land-rover', 'porsche', 'toyota', 'jeep', 'bmw'],
    editorsPicks: [
      { vehicle: '2025 Porsche Cayenne Turbo GT', price: 14990000, reason: 'The sports car of SUVs' },
      { vehicle: '2025 Land Rover Defender V8', price: 9890000, reason: 'Iconic capability' },
      { vehicle: '2025 Toyota Grand Highlander', price: 3999000, reason: 'Family value champion' },
      { vehicle: '2025 BMW X5 M60i', price: 7499000, reason: 'Balanced luxury performer' },
    ],
    priceTiers: [
      { label: 'Under $25K', count: 156, searchParams: 'bodyType=suv&priceMax=2500000' },
      { label: '$25K–$50K', count: 487, searchParams: 'bodyType=suv&priceMin=2500000&priceMax=5000000' },
      { label: '$50K–$100K', count: 412, searchParams: 'bodyType=suv&priceMin=5000000&priceMax=10000000' },
      { label: '$100K+', count: 188, searchParams: 'bodyType=suv&priceMin=10000000' },
    ],
    seo: { title: 'Used SUVs for Sale | BESPOKIAN Auto', description: 'Browse 1,243 certified pre-owned SUVs from Land Rover, Porsche, Toyota, and more. Inspected, guaranteed, delivered.' },
    gradient: 'from-[#1E2A3A] to-emerald-900/20',
  },
  {
    slug: 'trucks',
    name: 'Truck',
    pluralName: 'Trucks',
    description: 'American capability meets BESPOKIAN standards. Our truck collection spans hard-working half-tons to luxury-appointed heavy-duties. Every truck in our inventory has been inspected for mechanical integrity, bed condition, and towing readiness. From the jobsite to the ranch, find your next truck here.',
    tagline: 'Built to work, refined to impress',
    vehicleCount: 534,
    priceRange: { min: 2499000, max: 11500000 },
    popularBrands: ['ford', 'ram', 'chevrolet', 'gmc', 'toyota'],
    editorsPicks: [
      { vehicle: '2025 Ford F-150 Platinum', price: 5999000, reason: 'Best-selling for a reason' },
      { vehicle: '2025 RAM 1500 Limited', price: 6299000, reason: 'Luxury truck benchmark' },
      { vehicle: '2025 Toyota Tundra TRD Pro', price: 5799000, reason: 'Unmatched reliability' },
      { vehicle: '2025 GMC Sierra Denali Ultimate', price: 7199000, reason: 'Premium refinement' },
    ],
    priceTiers: [
      { label: 'Under $25K', count: 42, searchParams: 'bodyType=truck&priceMax=2500000' },
      { label: '$25K–$50K', count: 218, searchParams: 'bodyType=truck&priceMin=2500000&priceMax=5000000' },
      { label: '$50K–$100K', count: 224, searchParams: 'bodyType=truck&priceMin=5000000&priceMax=10000000' },
      { label: '$100K+', count: 50, searchParams: 'bodyType=truck&priceMin=10000000' },
    ],
    seo: { title: 'Used Trucks for Sale | BESPOKIAN Auto', description: 'Browse 534 certified pre-owned trucks from Ford, RAM, Chevrolet, and more. Inspected, guaranteed, delivered.' },
    gradient: 'from-[#1E2A3A] to-amber-900/20',
  },
  {
    slug: 'coupes',
    name: 'Coupe',
    pluralName: 'Coupes',
    description: 'Style distilled to its purest form. Coupes sacrifice nothing in pursuit of design and driving pleasure. Our collection ranges from accessible sport coupes to exotic grand tourers — each chosen for its ability to quicken the pulse. Two doors, unlimited character.',
    tagline: 'Pure style, uncompromised performance',
    vehicleCount: 312,
    priceRange: { min: 2299000, max: 34900000 },
    popularBrands: ['bmw', 'porsche', 'mercedes-benz', 'audi', 'lexus'],
    editorsPicks: [
      { vehicle: '2025 Porsche 911 Carrera S', price: 11999000, reason: 'The benchmark sports car' },
      { vehicle: '2025 BMW M4 Competition', price: 7899000, reason: 'Track-ready daily driver' },
      { vehicle: '2024 Lexus LC 500', price: 7299000, reason: 'Rolling sculpture' },
      { vehicle: '2025 Mercedes-AMG GT 63', price: 14990000, reason: 'Grand touring excellence' },
    ],
    priceTiers: [
      { label: 'Under $25K', count: 28, searchParams: 'bodyType=coupe&priceMax=2500000' },
      { label: '$25K–$50K', count: 89, searchParams: 'bodyType=coupe&priceMin=2500000&priceMax=5000000' },
      { label: '$50K–$100K', count: 124, searchParams: 'bodyType=coupe&priceMin=5000000&priceMax=10000000' },
      { label: '$100K+', count: 71, searchParams: 'bodyType=coupe&priceMin=10000000' },
    ],
    seo: { title: 'Used Coupes for Sale | BESPOKIAN Auto', description: 'Browse 312 certified pre-owned coupes from Porsche, BMW, Mercedes-Benz, and more. Style meets substance.' },
    gradient: 'from-[#1E2A3A] to-rose-900/20',
  },
  {
    slug: 'convertibles',
    name: 'Convertible',
    pluralName: 'Convertibles',
    description: 'Open-air motoring at its finest. A convertible transforms every drive into an event — wind in your hair, sun on your shoulders, engine note filling the sky. Our curated collection ensures you find the perfect drop-top, from weekend roadsters to four-season grand tourers.',
    tagline: 'Sky above, road ahead',
    vehicleCount: 187,
    priceRange: { min: 2799000, max: 38900000 },
    popularBrands: ['porsche', 'bmw', 'mercedes-benz', 'chevrolet', 'ford'],
    editorsPicks: [
      { vehicle: '2025 Porsche 718 Boxster GTS', price: 8999000, reason: 'Purist driving machine' },
      { vehicle: '2025 BMW M440i Convertible', price: 6499000, reason: 'Four-season elegance' },
      { vehicle: '2025 Chevrolet Corvette Stingray', price: 6999000, reason: 'American icon, open-top' },
      { vehicle: '2024 Mercedes-AMG SL 55', price: 12990000, reason: 'Luxury grand tourer' },
    ],
    priceTiers: [
      { label: 'Under $25K', count: 12, searchParams: 'bodyType=convertible&priceMax=2500000' },
      { label: '$25K–$50K', count: 48, searchParams: 'bodyType=convertible&priceMin=2500000&priceMax=5000000' },
      { label: '$50K–$100K', count: 78, searchParams: 'bodyType=convertible&priceMin=5000000&priceMax=10000000' },
      { label: '$100K+', count: 49, searchParams: 'bodyType=convertible&priceMin=10000000' },
    ],
    seo: { title: 'Used Convertibles for Sale | BESPOKIAN Auto', description: 'Browse 187 certified pre-owned convertibles from Porsche, BMW, and more. Open-air driving, BESPOKIAN guaranteed.' },
    gradient: 'from-[#1E2A3A] to-sky-900/20',
  },
  {
    slug: 'wagons',
    name: 'Wagon',
    pluralName: 'Wagons',
    description: 'The thinking enthusiast\'s choice. Wagons deliver the cargo space of an SUV with the driving dynamics of a sedan — and a dose of contrarian style. From the Audi RS6 Avant to the Volvo V60, our wagon collection is curated for drivers who refuse to compromise.',
    tagline: 'Practicality with a rebellious streak',
    vehicleCount: 98,
    priceRange: { min: 2899000, max: 13500000 },
    popularBrands: ['audi', 'volvo', 'mercedes-benz', 'bmw', 'subaru'],
    editorsPicks: [
      { vehicle: '2025 Audi RS6 Avant', price: 10990000, reason: 'The ultimate super-wagon' },
      { vehicle: '2025 Volvo V60 Cross Country', price: 4399000, reason: 'Scandinavian all-rounder' },
      { vehicle: '2025 Mercedes-AMG E 53 Wagon', price: 7899000, reason: 'Performance meets cargo' },
      { vehicle: '2024 Subaru Outback Wilderness', price: 3699000, reason: 'Adventure-ready value' },
    ],
    priceTiers: [
      { label: 'Under $25K', count: 8, searchParams: 'bodyType=wagon&priceMax=2500000' },
      { label: '$25K–$50K', count: 34, searchParams: 'bodyType=wagon&priceMin=2500000&priceMax=5000000' },
      { label: '$50K–$100K', count: 42, searchParams: 'bodyType=wagon&priceMin=5000000&priceMax=10000000' },
      { label: '$100K+', count: 14, searchParams: 'bodyType=wagon&priceMin=10000000' },
    ],
    seo: { title: 'Used Wagons for Sale | BESPOKIAN Auto', description: 'Browse 98 certified pre-owned wagons from Audi, Volvo, Mercedes-Benz, and more. The enthusiast\'s choice.' },
    gradient: 'from-[#1E2A3A] to-teal-900/20',
  },
  {
    slug: 'hatchbacks',
    name: 'Hatchback',
    pluralName: 'Hatchbacks',
    description: 'Urban agility wrapped in clever design. Hatchbacks punch above their weight with surprising cargo space, nimble handling, and often the most engaging driving dynamics in their class. From the hot hatch to the premium commuter, these are the cars that make city life better.',
    tagline: 'Clever design, urban agility',
    vehicleCount: 156,
    priceRange: { min: 1599000, max: 5500000 },
    popularBrands: ['volkswagen', 'mazda', 'honda', 'hyundai', 'mini'],
    editorsPicks: [
      { vehicle: '2025 Volkswagen Golf R', price: 4299000, reason: 'Hot hatch benchmark' },
      { vehicle: '2025 Mazda3 Turbo', price: 2999000, reason: 'Premium feel, attainable price' },
      { vehicle: '2025 MINI Cooper S', price: 3299000, reason: 'Iconic go-kart handling' },
      { vehicle: '2025 Hyundai Elantra N', price: 3199000, reason: 'Performance value king' },
    ],
    priceTiers: [
      { label: 'Under $25K', count: 72, searchParams: 'bodyType=hatchback&priceMax=2500000' },
      { label: '$25K–$50K', count: 68, searchParams: 'bodyType=hatchback&priceMin=2500000&priceMax=5000000' },
      { label: '$50K–$100K', count: 14, searchParams: 'bodyType=hatchback&priceMin=5000000&priceMax=10000000' },
      { label: '$100K+', count: 2, searchParams: 'bodyType=hatchback&priceMin=10000000' },
    ],
    seo: { title: 'Used Hatchbacks for Sale | BESPOKIAN Auto', description: 'Browse 156 certified pre-owned hatchbacks from Volkswagen, Mazda, Honda, and more.' },
    gradient: 'from-[#1E2A3A] to-violet-900/20',
  },
  {
    slug: 'minivans',
    name: 'Minivan',
    pluralName: 'Minivans',
    description: 'Unapologetically practical and surprisingly capable. The modern minivan is a family command center — sliding doors, three rows, entertainment systems, and enough cargo room for every soccer practice, road trip, and weekend project. Stop pretending you don\'t want one.',
    tagline: 'Family first, no compromises',
    vehicleCount: 124,
    priceRange: { min: 1999000, max: 5500000 },
    popularBrands: ['honda', 'toyota', 'chrysler', 'kia'],
    editorsPicks: [
      { vehicle: '2025 Honda Odyssey Elite', price: 4299000, reason: 'The benchmark family hauler' },
      { vehicle: '2025 Toyota Sienna Platinum', price: 4599000, reason: 'Hybrid efficiency leader' },
      { vehicle: '2025 Kia Carnival SX Prestige', price: 4199000, reason: 'Minivan meets luxury' },
      { vehicle: '2025 Chrysler Pacifica Pinnacle', price: 4899000, reason: 'Plug-in hybrid pioneer' },
    ],
    priceTiers: [
      { label: 'Under $25K', count: 32, searchParams: 'bodyType=minivan&priceMax=2500000' },
      { label: '$25K–$50K', count: 78, searchParams: 'bodyType=minivan&priceMin=2500000&priceMax=5000000' },
      { label: '$50K–$100K', count: 14, searchParams: 'bodyType=minivan&priceMin=5000000&priceMax=10000000' },
      { label: '$100K+', count: 0, searchParams: 'bodyType=minivan&priceMin=10000000' },
    ],
    seo: { title: 'Used Minivans for Sale | BESPOKIAN Auto', description: 'Browse 124 certified pre-owned minivans from Honda, Toyota, Chrysler, and more. Family-ready, BESPOKIAN guaranteed.' },
    gradient: 'from-[#1E2A3A] to-orange-900/20',
  },
  {
    slug: 'electric',
    name: 'Electric Vehicle',
    pluralName: 'Electric Vehicles',
    description: 'The future is here — and it drives beautifully. Electric vehicles offer instant torque, whisper-quiet cabins, and a driving experience unlike anything with a tailpipe. Our EV collection spans luxury sedans, performance SUVs, and adventure trucks. Each battery health-verified and ready for the road ahead.',
    tagline: 'Silent power, limitless potential',
    vehicleCount: 423,
    priceRange: { min: 2499000, max: 24900000 },
    popularBrands: ['tesla', 'rivian', 'lucid', 'bmw', 'porsche'],
    editorsPicks: [
      { vehicle: '2025 Tesla Model Y Performance', price: 4999000, reason: 'Best-selling EV, refined' },
      { vehicle: '2025 Porsche Taycan Turbo S', price: 17990000, reason: 'Electric performance king' },
      { vehicle: '2025 Rivian R1S Adventure', price: 7899000, reason: 'Electric adventure for all' },
      { vehicle: '2025 Lucid Air Grand Touring', price: 11990000, reason: 'Range champion (516 mi)' },
    ],
    priceTiers: [
      { label: 'Under $25K', count: 34, searchParams: 'bodyType=electric&priceMax=2500000' },
      { label: '$25K–$50K', count: 167, searchParams: 'bodyType=electric&priceMin=2500000&priceMax=5000000' },
      { label: '$50K–$100K', count: 148, searchParams: 'bodyType=electric&priceMin=5000000&priceMax=10000000' },
      { label: '$100K+', count: 74, searchParams: 'bodyType=electric&priceMin=10000000' },
    ],
    seo: { title: 'Used Electric Vehicles for Sale | BESPOKIAN Auto', description: 'Browse 423 certified pre-owned EVs from Tesla, Rivian, Lucid, and more. Battery-verified, guaranteed, delivered.' },
    gradient: 'from-[#1E2A3A] to-green-900/20',
  },
  {
    slug: 'luxury',
    name: 'Luxury Vehicle',
    pluralName: 'Luxury Vehicles',
    description: 'Where craftsmanship meets automotive excellence. Our luxury collection represents the pinnacle of the automobile — hand-stitched interiors, cutting-edge technology, and performance that stirs the soul. From established marques to emerging disruptors, each vehicle is hand-selected to meet the BESPOKIAN standard.',
    tagline: 'The pinnacle of automotive excellence',
    vehicleCount: 892,
    priceRange: { min: 3499000, max: 49900000 },
    popularBrands: ['mercedes-benz', 'bmw', 'porsche', 'bentley', 'rolls-royce'],
    editorsPicks: [
      { vehicle: '2025 Bentley Continental GT Speed', price: 27990000, reason: 'Grand touring perfection' },
      { vehicle: '2025 Porsche 911 GT3', price: 18490000, reason: 'Track-bred, road-ready' },
      { vehicle: '2025 Mercedes-Maybach S 680', price: 21500000, reason: 'Ultimate rear-seat luxury' },
      { vehicle: '2025 Rolls-Royce Ghost', price: 34900000, reason: 'The magic carpet ride' },
    ],
    priceTiers: [
      { label: 'Under $50K', count: 198, searchParams: 'bodyType=luxury&priceMax=5000000' },
      { label: '$50K–$100K', count: 312, searchParams: 'bodyType=luxury&priceMin=5000000&priceMax=10000000' },
      { label: '$100K–$200K', count: 267, searchParams: 'bodyType=luxury&priceMin=10000000&priceMax=20000000' },
      { label: '$200K+', count: 115, searchParams: 'bodyType=luxury&priceMin=20000000' },
    ],
    seo: { title: 'Used Luxury Cars for Sale | BESPOKIAN Auto', description: 'Browse 892 certified luxury vehicles from Mercedes-Benz, Porsche, Bentley, Rolls-Royce, and more.' },
    gradient: 'from-[#1E2A3A] to-[#D4A853]/30',
  },
];

export const BODY_TYPE_MAP = new Map(BODY_TYPES.map((bt) => [bt.slug, bt]));
